import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import {
  Store as StoreIcon,
  Restaurant as FastFoodIcon,
  LocalShipping as DeliveryIcon,
  Cloud as CloudIcon,
  DirectionsCar as TruckIcon,
  Coffee as CoffeeIcon,
  Settings as SettingsIcon,
  WhatsApp as WhatsAppIcon,
  Print as PrintIcon,
  Schedule as ScheduleIcon,
  SmartToy as AiIcon,
  Telegram as TelegramIcon,
  Loyalty as LoyaltyIcon
} from '@mui/icons-material';
import { useVendorConfig, useFeature } from '../context/VendorConfigContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useTheme } from '../context/ThemeContext';
import ToggleSwitch from '../components/ToggleSwitch';

interface BusinessTypeOption {
  value: string;
  label: string;
  description: string;
  icon: JSX.Element;
  features: {
    tableManagement: boolean;
    floorManagement: boolean;
    sectionManagement: boolean;
    orderLines: boolean;
    stewardFeatures: boolean;
    simpleMode: boolean;
  };
}

const businessTypes: BusinessTypeOption[] = [
  {
    value: 'FULL_SERVICE',
    label: 'Full Service Restaurant',
    description: 'Traditional restaurant with tables, waiters, and full dining experience',
    icon: <StoreIcon className="w-8 h-8" />,
    features: {
      tableManagement: true,
      floorManagement: true,
      sectionManagement: true,
      orderLines: true,
      stewardFeatures: true,
      simpleMode: false
    }
  },
  {
    value: 'QUICK_SERVICE',
    label: 'Quick Service (QSR)',
    description: 'Fast food or counter service with quick turnaround',
    icon: <FastFoodIcon className="w-8 h-8" />,
    features: {
      tableManagement: false,
      floorManagement: false,
      sectionManagement: false,
      orderLines: false,
      stewardFeatures: false,
      simpleMode: true
    }
  },
  {
    value: 'TAKEAWAY_ONLY',
    label: 'Takeaway Only',
    description: 'No dine-in facilities, only takeaway orders',
    icon: <DeliveryIcon className="w-8 h-8" />,
    features: {
      tableManagement: false,
      floorManagement: false,
      sectionManagement: false,
      orderLines: false,
      stewardFeatures: false,
      simpleMode: true
    }
  },
  {
    value: 'CLOUD_KITCHEN',
    label: 'Cloud Kitchen',
    description: 'Delivery-only kitchen with no customer-facing space',
    icon: <CloudIcon className="w-8 h-8" />,
    features: {
      tableManagement: false,
      floorManagement: false,
      sectionManagement: false,
      orderLines: false,
      stewardFeatures: false,
      simpleMode: true
    }
  },
  {
    value: 'FOOD_TRUCK',
    label: 'Food Truck',
    description: 'Mobile food vendor with limited space',
    icon: <TruckIcon className="w-8 h-8" />,
    features: {
      tableManagement: false,
      floorManagement: false,
      sectionManagement: false,
      orderLines: false,
      stewardFeatures: false,
      simpleMode: true
    }
  },
  {
    value: 'CAFE',
    label: 'Cafe',
    description: 'Coffee shop or cafe with simple table service',
    icon: <CoffeeIcon className="w-8 h-8" />,
    features: {
      tableManagement: true,
      floorManagement: false,
      sectionManagement: false,
      orderLines: true,
      stewardFeatures: false,
      simpleMode: false
    }
  }
];

const VendorConfigurationPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    configuration, 
    features, 
    loading, 
    error, 
    updateConfiguration, 
    enableSimpleMode, 
    disableSimpleMode 
  } = useVendorConfig();
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { theme } = useTheme();

  const handleBusinessTypeChange = async (businessType: string) => {
    try {
      setSaving(true);
      setSuccessMessage('');
      await updateConfiguration({ businessType });
      setSuccessMessage(`Business type changed to ${businessTypes.find(bt => bt.value === businessType)?.label}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to update business type:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSimpleMode = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      if (configuration?.simpleMode) {
        await disableSimpleMode();
        setSuccessMessage('Advanced features enabled');
      } else {
        await enableSimpleMode();
        setSuccessMessage('Simple mode enabled - Table management disabled');
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to toggle simple mode:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeature = async (feature: string, currentValue: boolean) => {
    try {
      setSaving(true);
      setSuccessMessage('');
      await updateConfiguration({ [feature]: !currentValue });
      setSuccessMessage(`${feature.replace(/([A-Z])/g, ' $1').trim()} ${!currentValue ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Failed to toggle ${feature}:`, err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/vendor/settings')}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3">
                <SettingsIcon className="h-6 w-6 text-gray-600" />
                <h1 className="text-xl font-semibold">Business Configuration</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Simple Mode Toggle */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-1 sm:mb-2">Simple Mode</h2>
              <p className="text-gray-600 text-sm sm:text-base">
                {configuration?.simpleMode 
                  ? 'Your restaurant is in simple mode. Table management features are disabled.'
                  : 'Your restaurant has full features enabled including table management.'}
              </p>
            </div>
            <button
              onClick={handleToggleSimpleMode}
              disabled={saving}
              className="rounded-lg text-sm sm:text-base font-medium transition-colors px-4 py-2 sm:px-6 sm:py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.primary,
                color: 'var(--text-inverse)',
                border: 'none',
                fontWeight: 600
              }}
              onMouseOver={(e) => (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'}
              onMouseOut={(e) => (e.currentTarget as HTMLButtonElement).style.opacity = '1'}
              onFocus={(e) => (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'}
              onBlur={(e) => (e.currentTarget as HTMLButtonElement).style.opacity = '1'}
            >
              {saving ? 'Saving...' : configuration?.simpleMode ? 'Enable Full Features' : 'Enable Simple Mode'}
            </button>
          </div>
          
          {configuration?.simpleMode && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Simple Mode Active</p>
                  <p>The following features are disabled:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>Table Management</li>
                    <li>Floor & Section Management</li>
                    <li>Order Lines (Running Bills)</li>
                    <li>Steward Features</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Business Type Selection */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-8">
          <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4">Business Type</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {businessTypes.map((type) => {
              const selected = configuration?.businessType === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => handleBusinessTypeChange(type.value)}
                  disabled={saving}
                  className="p-4 sm:p-6 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderColor: selected ? theme.primary : 'transparent',
                    background: selected 
                      ? `linear-gradient(135deg, ${theme.primary}40 0%, ${theme.primary}20 100%)`
                      : `linear-gradient(135deg, ${theme.gray?.[100] || '#f3f4f6'} 0%, ${theme.gray?.[50] || '#f9fafb'} 100%)`
                  }}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-2 sm:mb-3" style={{ color: selected ? theme.primary : theme.gray?.[600] }}>
                      {type.icon}
                    </div>
                    <h3 className="font-medium mb-1 text-sm sm:text-base" style={{ color: selected ? theme.primary : theme.text }}>
                      {type.label}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">{type.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feature Toggles */}
        {!configuration?.simpleMode && (
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
            <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4">Advanced Features</h2>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Table Management</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Manage tables, assign orders to specific tables</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableTableManagement || false}
                  onChange={() => handleToggleFeature('enableTableManagement', configuration?.enableTableManagement || false)}
                  disabled={saving}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Floor Management</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Organize tables into multiple floors</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableFloorManagement || false}
                  onChange={() => handleToggleFeature('enableFloorManagement', configuration?.enableFloorManagement || false)}
                  disabled={saving || !configuration?.enableTableManagement}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Section Management</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Divide floors into sections with different menus</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableSectionManagement || false}
                  onChange={() => handleToggleFeature('enableSectionManagement', configuration?.enableSectionManagement || false)}
                  disabled={saving || !configuration?.enableTableManagement}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Multiple Menus</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Create different menus for different sections or times</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableMultipleMenus || false}
                  onChange={() => handleToggleFeature('enableMultipleMenus', configuration?.enableMultipleMenus || false)}
                  disabled={saving}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Kitchen Display</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Digital kitchen order management system</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableKitchenDisplay || false}
                  onChange={() => handleToggleFeature('enableKitchenDisplay', configuration?.enableKitchenDisplay || false)}
                  disabled={saving}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Order Lines (Running Bills)</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Allow customers to add items to existing orders</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableOrderLines || false}
                  onChange={() => handleToggleFeature('enableOrderLines', configuration?.enableOrderLines || false)}
                  disabled={saving}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Steward Features</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Enable steward call button and management</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.enableStewardFeatures || false}
                  onChange={() => handleToggleFeature('enableStewardFeatures', configuration?.enableStewardFeatures || false)}
                  disabled={saving}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Guest Table Orders</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Allow customers to order from tables without logging in</p>
                </div>
                <ToggleSwitch
                  enabled={configuration?.allowGuestTableOrders ?? true}
                  onChange={() => handleToggleFeature('allowGuestTableOrders', configuration?.allowGuestTableOrders ?? true)}
                  disabled={saving || !configuration?.enableTableManagement}
                  size="sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <WhatsAppIcon className="h-5 w-5 text-green-600" />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold">Notification Settings</h2>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">WhatsApp Order Confirmation</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Send order confirmation via WhatsApp when customers place orders</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.enableWhatsappOrderConfirmation ?? true}
                onChange={() => handleToggleFeature('enableWhatsappOrderConfirmation', configuration?.enableWhatsappOrderConfirmation ?? true)}
                disabled={saving}
                // WhatsApp brand green — deliberate carve-out, same reasoning as the
                // Facebook button in Checkout: recolouring it would misrepresent the
                // integration. basil-todo
                color="#25D366"
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* Offers */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mt-8">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <LoyaltyIcon className="h-5 w-5" style={{ color: 'var(--action)' }} />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold">Offers</h2>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">
                  Apply offers automatically
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  On, a live offer comes off the bill by itself. Off, staff are shown
                  it and confirm first. Either way it appears on the bill before
                  payment and prints on the receipt.
                </p>
              </div>
              <ToggleSwitch
                enabled={configuration?.autoApplyOffers ?? true}
                onChange={() => handleToggleFeature('autoApplyOffers', configuration?.autoApplyOffers ?? true)}
                disabled={saving}
                ariaLabel="Apply offers automatically"
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* WhatsApp Ordering Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mt-8">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <WhatsAppIcon className="h-5 w-5 text-green-600" />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold">WhatsApp Ordering</h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Let customers order via WhatsApp after scanning QR codes
          </p>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">Enable WhatsApp Ordering</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Show WhatsApp ordering option when customers scan QR codes</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.enableWhatsappOrdering ?? false}
                onChange={() => handleToggleFeature('enableWhatsappOrdering', configuration?.enableWhatsappOrdering ?? false)}
                disabled={saving}
                // WhatsApp brand green — deliberate carve-out, same reasoning as the
                // Facebook button in Checkout: recolouring it would misrepresent the
                // integration. basil-todo
                color="#25D366"
                size="sm"
              />
            </div>
            {configuration?.enableWhatsappOrdering && (
              <div className="py-3 sm:py-4">
                <h3 className="font-medium text-sm sm:text-base text-gray-900 mb-1">WhatsApp Business Number</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-2">Your WhatsApp Business number in E.164 format (e.g., +919876543210)</p>
                <p className="text-sm font-mono bg-gray-50 rounded px-3 py-2 text-gray-700">
                  {configuration?.whatsappBusinessNumber || 'Not configured — contact admin'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Inventory Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mt-8">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <SettingsIcon className="h-5 w-5 text-amber-600" />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold">Inventory</h2>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">Inventory Management</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Track ingredients, recipes, stock levels and the production planner</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.enableInventoryManagement ?? true}
                onChange={() => handleToggleFeature('enableInventoryManagement', configuration?.enableInventoryManagement ?? true)}
                disabled={saving}
                size="sm"
              />
            </div>
            <div className="py-3 sm:py-4">
              <p className="text-xs sm:text-sm text-gray-500">
                Sales mode and stock deduction settings live in Inventory → Settings.
              </p>
            </div>
          </div>
        </div>

        {/* AI & Integrations */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mt-8">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <AiIcon className="h-5 w-5 text-indigo-600" />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold">AI &amp; Integrations</h2>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">AI assistant (web chat)</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Show the in-app assistant that answers questions and helps run day-to-day tasks</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.enableAiAssistant ?? false}
                onChange={() => handleToggleFeature('enableAiAssistant', configuration?.enableAiAssistant ?? false)}
                disabled={saving}
                size="sm"
              />
            </div>
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <TelegramIcon className="h-4 w-4 text-sky-500" />
                  <h3 className="font-medium text-sm sm:text-base text-gray-900">Telegram bot</h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Let staff record expenses, payables and stock from Telegram. Manage access in Staff Payments → Telegram access</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.enableTelegramBot ?? false}
                onChange={() => handleToggleFeature('enableTelegramBot', configuration?.enableTelegramBot ?? false)}
                disabled={saving}
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* Print Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mt-8">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <PrintIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold">Print Settings</h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Configure automatic printing to the desktop billing app (localhost:9100)
          </p>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">Auto-print KOT on Order Creation</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Automatically print Kitchen Order Tickets when new orders are placed</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.autoPrintKotOnOrder ?? false}
                onChange={() => handleToggleFeature('autoPrintKotOnOrder', configuration?.autoPrintKotOnOrder ?? false)}
                disabled={saving}
                size="sm"
              />
            </div>
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">Auto-print Invoice on Settlement</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Automatically print invoice/receipt when orders are settled</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.autoPrintInvoiceOnSettle ?? false}
                onChange={() => handleToggleFeature('autoPrintInvoiceOnSettle', configuration?.autoPrintInvoiceOnSettle ?? false)}
                disabled={saving}
                size="sm"
              />
            </div>
          </div>
        </div>
        {/* Operating Hours */}
        <div className="rounded-lg shadow-sm p-4 sm:p-6 mt-8" style={{ backgroundColor: theme.components.card.background, border: `1px solid ${theme.components.card.border}` }}>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <ScheduleIcon className="h-5 w-5" style={{ color: theme.primary }} />
            <h2 className="text-sm sm:text-base md:text-lg font-semibold" style={{ color: theme.text }}>Operating Hours</h2>
          </div>
          <p className="text-xs sm:text-sm mb-4" style={{ color: theme.gray?.[500] }}>
            Set your restaurant's operating hours and optionally block orders outside these times
          </p>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-3 py-3 sm:py-4" style={{ borderBottom: `1px solid ${theme.components.card.border}` }}>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm sm:text-base" style={{ color: theme.text }}>Enable Operating Hours</h3>
                <p className="text-xs sm:text-sm mt-0.5" style={{ color: theme.gray?.[500] }}>Display your restaurant's opening and closing times</p>
              </div>
              <ToggleSwitch
                enabled={configuration?.enableOperatingHours ?? false}
                onChange={() => handleToggleFeature('enableOperatingHours', configuration?.enableOperatingHours ?? false)}
                disabled={saving}
                color={theme.primary}
                size="sm"
              />
            </div>

            {configuration?.enableOperatingHours && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: theme.gray?.[600] }}>Opening Time</label>
                    <input
                      type="time"
                      value={configuration?.openingTime || '09:00'}
                      onChange={async (e) => {
                        try {
                          setSaving(true);
                          await updateConfiguration({ openingTime: e.target.value });
                          setSuccessMessage('Opening time updated');
                          setTimeout(() => setSuccessMessage(''), 3000);
                        } catch (err) {
                          console.error('Failed to update opening time:', err);
                        } finally {
                          setSaving(false);
                        }
                      }}
                      className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
                      style={{ backgroundColor: theme.components.input.background, border: `1px solid ${theme.components.input.border}`, color: theme.text, '--tw-ring-color': theme.components.input.focus } as React.CSSProperties}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: theme.gray?.[600] }}>Closing Time</label>
                    <input
                      type="time"
                      value={configuration?.closingTime || '22:00'}
                      onChange={async (e) => {
                        try {
                          setSaving(true);
                          await updateConfiguration({ closingTime: e.target.value });
                          setSuccessMessage('Closing time updated');
                          setTimeout(() => setSuccessMessage(''), 3000);
                        } catch (err) {
                          console.error('Failed to update closing time:', err);
                        } finally {
                          setSaving(false);
                        }
                      }}
                      className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
                      style={{ backgroundColor: theme.components.input.background, border: `1px solid ${theme.components.input.border}`, color: theme.text, '--tw-ring-color': theme.components.input.focus } as React.CSSProperties}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 py-3 sm:py-4" style={{ borderBottom: `1px solid ${theme.components.card.border}` }}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm sm:text-base" style={{ color: theme.text }}>Block Orders Outside Hours</h3>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: theme.gray?.[500] }}>Prevent customers from placing orders when the restaurant is closed</p>
                  </div>
                  <ToggleSwitch
                    enabled={configuration?.blockOrdersOutsideHours ?? false}
                    onChange={() => handleToggleFeature('blockOrdersOutsideHours', configuration?.blockOrdersOutsideHours ?? false)}
                    disabled={saving}
                    color={theme.primary}
                    size="sm"
                  />
                </div>

                {configuration?.blockOrdersOutsideHours && (
                  <div className="py-3">
                    <label className="block text-sm font-medium mb-1" style={{ color: theme.gray?.[600] }}>Closed Message</label>
                    <input
                      type="text"
                      value={configuration?.operatingHoursMessage || ''}
                      placeholder="We are currently closed. Please try again during our operating hours."
                      onChange={async (e) => {
                        try {
                          setSaving(true);
                          await updateConfiguration({ operatingHoursMessage: e.target.value });
                        } catch (err) {
                          console.error('Failed to update message:', err);
                        } finally {
                          setSaving(false);
                        }
                      }}
                      className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
                      style={{ backgroundColor: theme.components.input.background, border: `1px solid ${theme.components.input.border}`, color: theme.text, '--tw-ring-color': theme.components.input.focus } as React.CSSProperties}
                    />
                    <p className="text-xs mt-1" style={{ color: theme.gray?.[400] }}>Shown to customers when they try to order outside operating hours</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorConfigurationPage;